/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */

import * as Services from '@services';
import {
  Customer,
  CustomerSms,
  CustomerVehicle,
  GetCustomerInvite,
  PagedCustomerDto,
  PagedCustomerSmsRequest,
  PagedSelectCustomer,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class CustomerStore {
  customers: Map<string, Customer> = new Map();
  totalCount: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;
  loading: boolean = false;

  customer!: Customer | undefined;
  customerOwner!: Customer;

  selectCustomers: Map<string, Customer> = new Map();
  selectLoading = false;
  selectSkipCount = 0;
  selectPageSize = 10;
  selectTotalCount = 0;

  customerSmsInvite: Services.CustomerInvite | null = null;
  customerSmsInviteLoading: boolean = false;
  customerEstimateReminderInvite: Services.CustomerInvite | null = null;
  customerEstimateReminderInviteLoading: boolean = false;

  // All vehicles linked to the active customer (app + non-app).
  customerVehicles: CustomerVehicle[] = [];
  customerVehiclesLoading: boolean = false;

  // Customers historically linked to a searched registration number (booking suggestions).
  customersByReg: Customer[] = [];
  customersByRegLoading: boolean = false;

  // SMS / communication history for the active customer.
  smsHistory: Map<string, CustomerSms> = new Map();
  smsHistoryTotalCount: number = 0;
  smsHistorySkipCount: number = 0;
  smsHistoryCurrentPage: number = 1;
  smsHistoryMaxResultCount: number = 15;
  smsHistoryLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async getCustomerVehicles(customerId: string) {
    this.setCustomerVehiclesLoading(true);
    try {
      const result = await Services.CustomerService.getVehicles(customerId);
      runInAction(() => {
        this.customerVehicles = result;
      });
      this.setCustomerVehiclesLoading(false);
    } catch (error) {
      this.setCustomerVehiclesLoading(false);
      console.error(error);
    }
  }

  async getCustomersByReg(registrationNumber: string) {
    this.setCustomersByRegLoading(true);
    try {
      const result = await Services.CustomerService.getByVehicleReg(registrationNumber);
      runInAction(() => {
        this.customersByReg = result;
      });
      this.setCustomersByRegLoading(false);
    } catch (error) {
      this.setCustomersByRegLoading(false);
      console.error(error);
    }
  }

  async getSmsHistory(request: PagedCustomerSmsRequest) {
    this.setSmsHistoryLoading(true);
    try {
      const result = await Services.CustomerService.getSmsHistory(request);
      runInAction(() => {
        this.smsHistory.clear();
        result.items.forEach((sms: CustomerSms) => {
          this.smsHistory.set(sms.id, sms);
        });
        this.smsHistoryTotalCount = result.totalCount;
      });
      this.setSmsHistoryLoading(false);
    } catch (error) {
      this.setSmsHistoryLoading(false);
      console.error(error);
    }
  }

  async getAll(pagedFilterAndSortedRequest: PagedCustomerDto) {
    this.setLoading(true);
    try {
      const result = await Services.CustomerService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        this.customers.clear();
        result.items.forEach((customer: Customer) => {
          this.customers.set(customer.id, customer);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getSelectCustomers(request: PagedSelectCustomer) {
    this.setSelectLoading(true);
    try {
      const result = await Services.CustomerService.getAll(request);
      runInAction(() => {
        result.items.forEach((customer: Customer) => {
          this.selectCustomers.set(customer.id, customer);
        });
        this.selectTotalCount = result.totalCount;
      });
      this.setSelectLoading(false);
    } catch (error) {
      this.setSelectLoading(false);
      console.error(error);
    }
  }

  async create(customer: Services.CreateCustomer) {
    this.setLoading(true);
    try {
      const response = await Services.CustomerService.create(customer);
      runInAction(() => {
        const newCustomer = response.result;

        // Add the new customer to the beginning of the map
        const tempArray = Array.from(this.customers.entries());
        tempArray.unshift([newCustomer.id, newCustomer]);

        // If there are more than 10 customers, remove the oldest one
        if (tempArray.length > 10) {
          tempArray.pop();
        }

        // Clear the map and add the updated list back to it
        this.customers.clear();
        tempArray.forEach(([id, customer]) => {
          this.customers.set(id, customer);
        });

        this.totalCount += 1;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully created!',
      });
      return response.result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return null;
    }
  }

  async update(customer: Services.UpdateCustomer): Promise<Customer | null> {
    this.setLoading(true);
    try {
      const result: Customer = await Services.CustomerService.update(customer);
      runInAction(() => {
        if (this.customer && this.customer.id === result.id) {
          this.customer = result;
        }
        this.customers.set(result.id, result);
        // The pickers keep their own copies; left alone, re-picking the customer shows the old details.
        if (this.selectCustomers.has(result.id)) {
          this.selectCustomers.set(result.id, result);
        }
        this.customersByReg = this.customersByReg.map((c) => (c.id === result.id ? result : c));
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully updated!',
      });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return null;
    }
  }

  async get(entityDto: Services.EntityDto<string | number>) {
    this.setLoading(true);
    try {
      const result = await Services.CustomerService.get(entityDto);
      runInAction(() => {
        this.customer = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getByUserId(entityDto: Services.EntityDto<string | number>) {
    const result = await Services.CustomerService.getByUserId(entityDto);
    runInAction(() => {
      this.customer = result;
    });
  }

  async delete(entityDto: Services.EntityDto<string>) {
    await Services.CustomerService.delete(entityDto);
    runInAction(() => {
      this.customers.delete(entityDto.id);
      this.totalCount -= 1;
      this.currentPage =
        this.customers.size === 0 && this.currentPage > 0 ? this.currentPage - 1 : this.currentPage;
      this.skipCount = this.currentPage > 0 ? (this.currentPage - 1) * 10 : 0;
    });
  }

  async getSmsInvite(request: GetCustomerInvite) {
    this.setCustomerSmsInviteLoading(true);
    try {
      const result = await Services.CustomerService.getInvite(request);
      runInAction(() => {
        this.customerSmsInvite = result;
      });
      this.setCustomerSmsInviteLoading(false);
    } catch (error) {
      this.setCustomerSmsInviteLoading(false);
      console.error(error);
    }
  }

  async getEstimateReminderInvite(request: GetCustomerInvite) {
    this.setCustomerEstimateReminderInviteLoading(true);
    try {
      const result = await Services.CustomerService.getInvite(request);
      runInAction(() => {
        this.customerEstimateReminderInvite = result;
      });
      this.setCustomerEstimateReminderInviteLoading(false);
    } catch (error) {
      this.setCustomerEstimateReminderInviteLoading(false);
      console.error(error);
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setCustomerSmsInviteLoading = (value: boolean) => {
    runInAction(() => {
      this.customerSmsInviteLoading = value;
    });
  };

  setCustomerEstimateReminderInviteLoading = (value: boolean) => {
    runInAction(() => {
      this.customerEstimateReminderInviteLoading = value;
    });
  };

  clearCustomerSmsInvite = () => {
    runInAction(() => {
      this.customerSmsInvite = null;
    });
  };

  clearCustomerEstimateReminderInvite = () => {
    runInAction(() => {
      this.customerEstimateReminderInvite = null;
    });
  };

  setCustomerSmsInvite = (customerSmsInvite: Services.CustomerInvite) => {
    runInAction(() => {
      this.customerSmsInvite = customerSmsInvite;
    });
  };

  setCustomerEstimateReminderInvite = (customerEstimateReminderInvite: Services.CustomerInvite) => {
    runInAction(() => {
      this.customerEstimateReminderInvite = customerEstimateReminderInvite;
    });
  };

  setCustomerVehiclesLoading = (value: boolean) => {
    runInAction(() => {
      this.customerVehiclesLoading = value;
    });
  };

  clearCustomerVehicles = () => {
    runInAction(() => {
      this.customerVehicles = [];
    });
  };

  setCustomersByRegLoading = (value: boolean) => {
    runInAction(() => {
      this.customersByRegLoading = value;
    });
  };

  clearCustomersByReg = () => {
    runInAction(() => {
      this.customersByReg = [];
    });
  };

  setSmsHistoryLoading = (value: boolean) => {
    runInAction(() => {
      this.smsHistoryLoading = value;
    });
  };

  setSmsHistoryMaxResultCount = (value: number) => {
    runInAction(() => {
      this.smsHistoryMaxResultCount = value;
    });
  };

  setSmsHistorySkipCount = (value: number) => {
    runInAction(() => {
      this.smsHistorySkipCount = value;
    });
  };

  setSmsHistoryCurrentPage = (value: number) => {
    runInAction(() => {
      this.smsHistoryCurrentPage = value;
    });
  };

  resetSmsHistory = () => {
    runInAction(() => {
      this.smsHistory.clear();
      this.smsHistoryTotalCount = 0;
      this.smsHistorySkipCount = 0;
      this.smsHistoryCurrentPage = 1;
    });
  };

  clearCustomer = () => {
    runInAction(() => {
      this.customer = undefined;
    });
  };

  clearCustomers = () => {
    runInAction(() => {
      this.customers.clear();
    });
  };

  resetCustomers = () => {
    runInAction(() => {
      this.customers.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  };

  setMaxResultCount = (value: number) => {
    runInAction(() => {
      this.maxResultCount = value;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  clearSelectCustomers = () => {
    runInAction(() => {
      this.selectCustomers.clear();
      this.selectTotalCount = 0;
    });
  };

  setSelectLoading = (value: boolean) => {
    runInAction(() => {
      this.selectLoading = value;
    });
  };

  setSelectSkipCount = (value: number) => {
    runInAction(() => {
      this.selectSkipCount = value;
    });
  };

  resetSelectServices = () => {
    runInAction(() => {
      this.selectCustomers.clear();
      this.selectTotalCount = 0;
    });
  };
}

export default CustomerStore;
